import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useFetch, useDelete, usePut } from '../../../components/component-items/helpers'
import { globalState } from '../../../store'
import { StatusBoxStyled } from '../../../components/component-items/status-box'
import { Form, InputGroup } from 'react-bootstrap'

// Components
import GlobalStyles from '../../../components/component-items/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBagShopping, faTruckContainer } from '@fortawesome/pro-duotone-svg-icons'
import { DragNDrop } from '../../../components/component-items/dragndrop'
import Checkbox from '../../../components/component-items/checkbox'

const Container = styled.div`
  margin: 2em auto;
  width: 60%;
  color: ${(props) => props.theme.colors.black};
  .editor-container {
    background: #f7f7f7;
    border-radius: 4px;
    padding: 0.5em 1em;
    margin: 1em 0;
  }
`

const PackingSlipImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    border: 1px solid grey;
    width: 100%;
    max-width: 400px;
    height: auto;
  }
`

const SectionDivider = styled.hr`
  margin: 2.5em 0;
  border-top: 1px solid #dee2e6;
`

const TabRow = styled.div`
  display: flex;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 1.5rem;
`

const TabButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  font-size: 1rem;
  font-weight: 400;
  background: #f5f5f5;
  color: #6c757d;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  transition: all 0.15s ease;

  &:hover {
    background: #eef0ff;
    color: #5d78ff;
  }

  &.active {
    background: #fff;
    color: #5d78ff;
    font-weight: 600;
    border-bottom-color: #5d78ff;
  }

  svg {
    height: 1.2em;
    width: 1.2em;
  }
`

export const PackingSlip = () => {
  const {
    state: { csrf, companyId },
  } = globalState()
  const { response: resp, loaded, error }: any = useFetch(`/api/company/${companyId}/packing-slip/`, {})
  const slipId = loaded && resp ? resp.id : null

  const imagePresent = loaded && resp ? resp.image : null
  const image = loaded && resp ? <img src={resp.image} style={{ width: 200 }} /> : null

  const [shippingName, setShippingName] = useState('')
  const [QRDefault, setQRDefault] = useState('ecommerce_checkout_url')
  const [QRURL, setQRURL] = useState('')
  const [pricing, setPricing] = useState(true)
  const [d2cMode, setD2cMode] = useState('generic_label')
  const [b2bMode, setB2bMode] = useState('disabled')
  const [paperHeader, setPaperHeader] = useState('')
  const [paperSubheader, setPaperSubheader] = useState('')
  const [paperFooter, setPaperFooter] = useState('')
  const packingSlipFee = (loaded && resp?.packing_slip_fee) || '0.50'
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [thermalLoaded, setThermalLoaded] = useState(false)

  useEffect(() => {
    if (!slipId || b2bMode !== 'full_size') return
    let currentUrl: string | null = null
    const params = new URLSearchParams()
    if (paperHeader) params.set('header', paperHeader)
    if (paperSubheader) params.set('subheader', paperSubheader)
    if (paperFooter) params.set('footer', paperFooter)
    const timeout = setTimeout(() => {
      fetch(`/api/company/${companyId}/packing-slip/${slipId}/paper-preview/?${params.toString()}`)
        .then((r) => r.blob())
        .then((blob) => {
          currentUrl = URL.createObjectURL(blob)
          setPdfBlobUrl(currentUrl)
        })
        .catch(() => setPdfBlobUrl(null))
    }, 800)
    return () => {
      clearTimeout(timeout)
      if (currentUrl) URL.revokeObjectURL(currentUrl)
    }
  }, [slipId, b2bMode, companyId, paperHeader, paperSubheader, paperFooter])

  const [activeTab, setActiveTab] = useState('D2C')
  const [file, setFile] = useState(null)

  useEffect(() => {
    if (loaded && resp) {
      setShippingName(resp.shipping_name || '')
      setQRDefault(resp.qr_default || '')
      setQRURL(resp.qr_static_url || '')
      setPricing(resp.pricing)
      setD2cMode(resp.d2c_packing_slip_mode || 'generic_label')
      setB2bMode(resp.b2b_packing_slip_mode || 'disabled')
      setPaperHeader(resp.paper_packing_slip_header || '')
      setPaperSubheader(resp.paper_packing_slip_subheader || '')
      setPaperFooter(resp.paper_packing_slip_footer || '')
    }
  }, [loaded])

  const uploadFile = async () => {
    var formData = new window.FormData()
    if (file) {
      formData.append('image', file, 'logo.png')
    }

    const reload = true
    const stringifyBody = false
    formData.append('shipping_name', shippingName)
    formData.append('qr_default', QRDefault)
    formData.append('qr_static_url', QRURL)
    // @ts-ignore
    formData.append('pricing', pricing)
    formData.append('d2c_packing_slip_mode', d2cMode)
    formData.append('b2b_packing_slip_mode', b2bMode)
    formData.append('paper_packing_slip_header', paperHeader)
    formData.append('paper_packing_slip_subheader', paperSubheader)
    formData.append('paper_packing_slip_footer', paperFooter)
    usePut(`/api/company/${companyId}/packing-slip/${slipId}/`, formData, csrf, reload, stringifyBody)
  }

  const deleteLogo = async () => {
    useDelete(`/api/company/${companyId}/packing-slip/${slipId}/delete_logo/`, csrf, true, false)
  }

  return (
    <GlobalStyles.FullPageCard>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>Packing Slip</h3>
        </GlobalStyles.CardTitle>
      </GlobalStyles.CardHeader>
      <Container>
        <Form.Label>Company Name (As Shown on Packing Slip)</Form.Label>
        <Form.Control type="text" name="name" value={shippingName} onChange={(e) => setShippingName(e.target.value)} />
        <Form.Label style={{ marginTop: '2em' }}>Company Logo</Form.Label>
        {imagePresent ? (
          <GlobalStyles.Button
            onClick={() => deleteLogo()}
            style={{ minWidth: 150, marginTop: '2em', float: 'right', background: 'red' }}
          >
            Delete Logo
          </GlobalStyles.Button>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'center' }}>{image}</div>
        <DragNDrop {...{ file, setFile }} fileTypes={'.jpg, .jpeg, .png'} />

        <SectionDivider />

        <TabRow>
          <TabButton className={activeTab === 'D2C' ? 'active' : ''} onClick={() => setActiveTab('D2C')}>
            <FontAwesomeIcon icon={faBagShopping} />
            D2C
          </TabButton>
          <TabButton className={activeTab === 'B2B' ? 'active' : ''} onClick={() => setActiveTab('B2B')}>
            <FontAwesomeIcon icon={faTruckContainer} />
            B2B
          </TabButton>
        </TabRow>

        {activeTab === 'D2C' && (
          <>
            <Form.Label>
              <strong>D2C Packing Slip Mode</strong>
            </Form.Label>
            <Form.Select value={d2cMode} onChange={(e) => setD2cMode(e.target.value)}>
              <option value="disabled">Disabled</option>
              <option value="generic_label">Generic Label (Free)</option>
            </Form.Select>

            {d2cMode === 'generic_label' && (
              <>
                <StatusBoxStyled className={'purple'} style={{ margin: '2em 0' }}>
                  QR codes are added to 4&apos; x 6&apos; thermal slips and allow the customer to view their virtual
                  packing slip via your shopify checkout page, a static URL given to all merchants below, or optionally
                  Kase&apos;s packing-slip page if none of those are provided.
                </StatusBoxStyled>
                <Form.Label>Default QR Code URL</Form.Label>
                <Form.Select value={QRDefault} onChange={(e) => setQRDefault(e.target.value)}>
                  <option value="ecommerce_checkout_url">Ecommerce Checkout URL</option>
                  <option value="static_url">Static URL</option>
                  <option value="internal_url">Kase URL (Virtual Packslip)</option>
                  <option value="blank">None</option>
                </Form.Select>
                <Form.Label style={{ marginTop: '2em' }}>Static QR Code URL (default for manual orders)</Form.Label>
                <Form.Control type="text" value={QRURL} maxLength={200} onChange={(e) => setQRURL(e.target.value)} />
                <InputGroup style={{ marginTop: '2em' }}>
                  <Checkbox selected={pricing} setSelected={() => setPricing(!pricing)} />
                  <p style={{ padding: '0 1em', lineHeight: '24px' }}>
                    Include Pricing (Pulled from e-commerce checkout)
                  </p>
                </InputGroup>
                <Form.Label style={{ marginTop: '2em' }}>Preview Packing Slip</Form.Label>
                <PackingSlipImage>
                  {loaded && slipId ? (
                    <>
                      <img
                        src={`/api/company/${companyId}/packing-slip/${slipId}/preview/`}
                        onLoad={() => setThermalLoaded(true)}
                        onError={() => setThermalLoaded(true)}
                        style={{ display: thermalLoaded ? 'block' : 'none' }}
                      />
                      {!thermalLoaded && <p style={{ color: '#6c757d' }}>Loading preview...</p>}
                    </>
                  ) : (
                    <p style={{ color: '#6c757d' }}>Loading preview...</p>
                  )}
                </PackingSlipImage>
              </>
            )}
          </>
        )}

        {activeTab === 'B2B' && (
          <>
            <Form.Label>
              <strong>B2B Packing Slip Mode</strong>
            </Form.Label>
            <Form.Select value={b2bMode} onChange={(e) => setB2bMode(e.target.value)}>
              <option value="disabled">Disabled</option>
              <option value="generic_label">Generic Label (Free)</option>
              <option value="full_size">Full-size Paper (${packingSlipFee}/slip)</option>
            </Form.Select>

            {b2bMode === 'full_size' && (
              <>
                <Form.Label style={{ marginTop: '1.5em' }}>Header Text</Form.Label>
                <Form.Control
                  type="text"
                  value={paperHeader}
                  maxLength={500}
                  placeholder="Displayed above the packing slip title"
                  onChange={(e) => setPaperHeader(e.target.value)}
                />
                <Form.Label style={{ marginTop: '1em' }}>Subheader Text</Form.Label>
                <Form.Control
                  type="text"
                  value={paperSubheader}
                  maxLength={500}
                  placeholder="Displayed below the packing slip title"
                  onChange={(e) => setPaperSubheader(e.target.value)}
                />
                <Form.Label style={{ marginTop: '1em' }}>Footer Text</Form.Label>
                <Form.Control
                  type="text"
                  value={paperFooter}
                  maxLength={500}
                  placeholder="Displayed at the bottom of the page"
                  onChange={(e) => setPaperFooter(e.target.value)}
                />
                <p style={{ marginTop: '1em', fontSize: '0.85em', color: '#6c757d' }}>
                  The company logo uploaded above will be used on the paper packing slip.
                </p>
                <Form.Label style={{ marginTop: '1.5em' }}>Preview Paper Packing Slip</Form.Label>
                <PackingSlipImage>
                  {pdfBlobUrl ? (
                    <iframe
                      src={pdfBlobUrl}
                      style={{ width: '100%', height: '500px', border: '1px solid #dee2e6' }}
                      title="Paper Packing Slip Preview"
                    />
                  ) : (
                    <p style={{ color: '#6c757d' }}>Loading preview...</p>
                  )}
                </PackingSlipImage>
              </>
            )}
          </>
        )}

        <GlobalStyles.Button onClick={() => uploadFile()} style={{ minWidth: 150, marginTop: '2em', float: 'right' }}>
          Submit
        </GlobalStyles.Button>
      </Container>
    </GlobalStyles.FullPageCard>
  )
}
